import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import styles from './LoginScreen.module.css';
import { apiService } from '../../../utils/api';
import { checkProfileCompletion } from '../../../utils/profileFlow';
import logoSecondLane from '../../../assets/SL-Top-Icon.png';

// LoginScreen
const LoginScreen: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Check for message in location state (e.g., from registration)
  useEffect(() => {
    if (location.state && 'message' in location.state) {
      setMessage(location.state.message as string);
      // Clear the message from location state
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Form validation
    if (!email || !password) {
      setError('Please fill in all fields');
      return;
    }

    try {
      setLoading(true);
      // Call API for authentication
      const response = await apiService.login({
        email,
        password
      });
      // Store token in localStorage
      localStorage.setItem('authToken', response.token);
      // Force a storage event to trigger auth state update
      window.dispatchEvent(new StorageEvent('storage', {
        key: 'authToken',
        newValue: response.token,
        storageArea: localStorage
      }));
      // Check attestation status
      const attestationStatus = await apiService.getAttestationStatus();
      
      // Redirect based on attestation status
      if (!attestationStatus.is_attested) {
        navigate('/welcome');
      } else {
        // Check if profile is complete
        const profileCheck = await checkProfileCompletion();
        if (profileCheck.shouldRedirect) {
          // Redirect to profile completion with country if available
          navigate('/complete-profile', {
            state: profileCheck.country ? { country: profileCheck.country } : undefined
          });
        } else {
          navigate('/deals');
        }
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Invalid email or password';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.headerLogoContainer}>
        <img src={logoSecondLane} alt="SecondLane" className={styles.headerLogo} />
      </div>
      
      <div className={styles.formContainer}>
        <h1 className={styles.title}>Log In</h1>
        
        <p className={styles.description}>
          Sign in to access your SecondLane account
        </p>
        
        {message && (
          <div className={styles.message}>
            <span className={styles.messageIcon}>✓</span>
            {message}
          </div>
        )}
        
        {error && (
          <div className={styles.error}>
            <span className={styles.errorIcon}>!</span>
            {error}
          </div>
        )}
        
        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.formGroup}>
            <label htmlFor="email" className={styles.label}>Email</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter email"
              disabled={loading}
              className={styles.input}
              required
            />
          </div>
          
          <div className={styles.formGroup}>
            <label htmlFor="password" className={styles.label}>Password</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter password"
              disabled={loading}
              className={styles.input}
              required
            />
            <div className={styles.forgotPasswordContainer}>
              <Link to="/forgot-password" className={styles.forgotPasswordLink}>
                Forgot Password?
              </Link>
            </div>
          </div>
          
          <div className={styles.navigationButtons}>
            <button 
              type="submit" 
              className={`${styles.submitButton} ${!loading ? styles.activeButton : ''}`}
              disabled={loading}
            >
              {loading ? 'Logging in...' : 'Log In'}
            </button>
          </div>
        </form>
        
        <div className={styles.links}>
          <p>
            Don't have an account? <Link to="/signup" className={styles.link}>Sign Up</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginScreen; 